import React from "react";
import { Navigate } from "react-router-dom";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { Loader2 } from "lucide-react";

/**
 * Wraps any /admin/* route so unauthenticated visitors are bounced to
 * the login page. Centralized to avoid copy-pasting the redirect into
 * every page component. Shows a brief loader during the initial token
 * verification round-trip to /api/auth/me — without this the user sees
 * a flash of "not authed → redirect → re-login" even when their token
 * is valid on refresh.
 */
export default function RequireAdmin({ children, permission }) {
  const { isAuthenticated, verified, hasPermission } = useAdminAuth();
  // If we have no token at all, redirect immediately — there's nothing to
  // verify. `verified` only flips to true once the /auth/me probe returns,
  // which never fires for an empty-token state.
  if (!isAuthenticated) return <Navigate to="/admin/login" replace />;
  // Token present but probe hasn't completed yet → show a brief spinner
  // so a stale token doesn't flash protected UI before the redirect.
  if (!verified) {
    return (
      <main className="min-h-screen bg-black text-white grid place-items-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#F37021]" />
      </main>
    );
  }
  if (permission && !hasPermission(permission)) {
    return (
      <main className="min-h-screen bg-black text-white grid place-items-center px-6 text-center">
        <div>
          <div className="text-sm font-semibold text-white">Access denied</div>
          <div className="mt-2 text-xs text-white/50">Your admin role does not include this area.</div>
        </div>
      </main>
    );
  }
  return children;
}
